/**
 * The room photographs behind the size preview, and where to put them so
 * their furniture measures what the caption promises.
 *
 * Kept apart from the component because it is the load-bearing part: the room
 * preview exists to answer "how big is 60 x 40 really?", and it can only answer
 * honestly if the furniture in the picture is drawn at the same
 * centimetre-to-pixel factor as the artwork. A photograph has no inherent
 * scale, so it gets one here — from measured pixel edges and a declared real
 * width — and that arithmetic is worth testing rather than eyeballing.
 *
 * Both are 3D renders from Pexels (free for commercial use, no attribution
 * required): pexels.com/photo/20390758 and pexels.com/photo/20390768. The blank
 * mockup frame each one had on the wall was painted out, so the customer's
 * piece is the only thing hanging there. Replace a picture and its numbers
 * must be re-measured, or the preview quietly starts lying about size.
 */

export type RoomPhotoScene = "sofa" | "bed";

export interface RoomPhoto {
  src: string;
  /** Pixel size of the file. */
  width: number;
  height: number;
  /** Outer edges of the reference furniture, in file pixels. */
  furnitureLeftPx: number;
  furnitureRightPx: number;
  /** Where the wall meets the floor, in file pixels from the top. */
  floorPx: number;
  /** Real width of the reference furniture. */
  furnitureCm: number;
  /** The window onto the room: how much wall to show, around the furniture. */
  viewCmWide: number;
  viewCmAbove: number;
  viewCmBelow: number;
  /** Where the centre of the piece hangs, above the floor. */
  hangCentreCm: number;
  /**
   * Wall to keep below the piece, which is how much of the furniture comes
   * into shot. Per photograph, because the reference sits at a different
   * height in each: a sofa back stops around 76 cm, while a bed is mostly
   * below that and needs more wall beneath the piece before anything
   * recognisable appears.
   */
  contextBelowCm: number;
}

export const ROOM_PHOTOS: Record<RoomPhotoScene, RoomPhoto> = {
  sofa: {
    src: "/room/living-brick-plants.jpg",
    width: 1600,
    height: 900,
    // Outer edges of the sofa's arms.
    furnitureLeftPx: 575,
    furnitureRightPx: 1097,
    floorPx: 682,
    furnitureCm: 200,
    // Close enough that the sofa fills most of the frame, wide enough that the
    // monstera beside it is in shot.
    viewCmWide: 340,
    viewCmAbove: 205,
    viewCmBelow: 28,
    // Where people actually hang a picture: centre about 152 cm off the floor.
    hangCentreCm: 152,
    // Reaches the back of the sofa, which is all of it the eye needs.
    contextBelowCm: 72,
  },
  bed: {
    src: "/room/bedroom-olive-plants.jpg",
    width: 1600,
    height: 900,
    // The headboard, which stands against the wall the piece hangs on.
    furnitureLeftPx: 668,
    furnitureRightPx: 1033,
    floorPx: 688,
    // A standard double.
    furnitureCm: 160,
    viewCmWide: 380,
    // Higher than the sofa's: the piece hangs higher, and a 90 cm one must
    // still clear the top of the frame.
    viewCmAbove: 215,
    viewCmBelow: 28,
    // Above a bed the piece sits higher, clear of the headboard.
    hangCentreCm: 165,
    // More than the sofa needs: the piece hangs higher here and the bed
    // itself is lower, so a 72 cm reach showed nothing but pillow tips and
    // the room stopped saying anything about size.
    contextBelowCm: 105,
  },
};

export interface RoomPhotoLayout {
  /** The scene's centimetre-to-pixel factor. */
  pxPerCm: number;
  sceneWidth: number;
  sceneHeight: number;
  /** Where the photograph goes inside the scene, in pixels. */
  img: { left: number; top: number; width: number; height: number };
  /** The floor line, in pixels from the top of the scene. */
  floorY: number;
}

/** Wall left above the piece, so it never sits jammed against the crop. */
const HEADROOM_CM = 18;

/**
 * The window onto the room, framed for the piece that is actually hanging.
 *
 * `viewCmAbove`, `viewCmBelow` and `viewCmWide` on the photograph are the
 * widest case: floor to well above a 90 cm piece, wide enough to keep the
 * plant beside the sofa in shot. Used for every size they framed a 40 x 60 as
 * if a 60 x 90 were on the wall, and always from the floor up — so the print
 * sat small in the middle of somebody else's composition.
 *
 * Given the piece, the window closes in on it. Vertically it runs from a
 * little above the piece down to CONTEXT_BELOW_CM beneath it, which lands
 * around the top of the furniture; the floor and the lower half of the sofa
 * fall outside, and that is the point. Horizontally it takes whatever width
 * the box then has at that scale, so the room always fills the frame.
 *
 * It never opens past the photograph's own measured framing, so this can crop
 * closer but never invent wall nobody photographed.
 *
 * Scale is untouched. Whatever is still in shot spans its real width at the
 * same centimetre-to-pixel factor as the piece — a closer camera, not a
 * bigger print.
 */
function windowFor(
  photo: RoomPhoto,
  piece?: { widthCm: number; heightCm: number },
): { wide: number; above: number; below: number } {
  if (!piece || piece.widthCm <= 0 || piece.heightCm <= 0) {
    return { wide: photo.viewCmWide, above: photo.viewCmAbove, below: photo.viewCmBelow };
  }

  const above = Math.min(
    photo.viewCmAbove,
    photo.hangCentreCm + piece.heightCm / 2 + HEADROOM_CM,
  );

  // Where the window's lower edge sits, measured from the floor. Positive
  // means the floor itself is cropped away; `below` is the same number in the
  // photograph's own sense, which counts downwards from the floor line.
  const bottomAboveFloor =
    photo.hangCentreCm - piece.heightCm / 2 - photo.contextBelowCm;
  const below = Math.min(photo.viewCmBelow, -bottomAboveFloor);

  return { wide: photo.viewCmWide, above, below };
}

/**
 * Fit the room's window into a box, and place the photograph so its
 * furniture spans its real width at that scale.
 */
export function roomPhotoLayout(
  photo: RoomPhoto,
  boxWidth: number,
  maxHeight: number,
  piece?: { widthCm: number; heightCm: number },
): RoomPhotoLayout | null {
  if (boxWidth <= 0 || maxHeight <= 0) return null;

  const view = windowFor(photo, piece);
  const viewCmTall = view.above + view.below;
  // Whichever axis binds first, so the room never overflows its box.
  /**
   * With a piece to frame, the vertical window decides the scale and the
   * width simply follows it.
   *
   * Taking the smaller of the two fits, as the unframed case does, hands the
   * decision back to the width — and since the width is whatever the
   * photograph was measured at, every piece came out at the same distance
   * again however tightly the vertical window closed in. The room can be
   * cropped horizontally as freely as vertically, so nothing is lost by
   * letting height lead.
   */
  const pxPerCm = piece
    ? maxHeight / viewCmTall
    : Math.min(boxWidth / view.wide, maxHeight / viewCmTall);

  // However much wall that scale leaves room for, never more than was
  // photographed. When the photograph runs out first the room sits narrower
  // than its container rather than showing a band of grey beside it.
  const wide = Math.min(photo.viewCmWide, boxWidth / pxPerCm);

  const sceneWidth = wide * pxPerCm;
  const sceneHeight = viewCmTall * pxPerCm;

  // The one line that ties the photograph to the scene's scale.
  const spanPx = photo.furnitureRightPx - photo.furnitureLeftPx;
  const scale = (photo.furnitureCm * pxPerCm) / spanPx;

  const centrePx = (photo.furnitureLeftPx + photo.furnitureRightPx) / 2;
  const floorY = view.above * pxPerCm;

  return {
    pxPerCm,
    sceneWidth,
    sceneHeight,
    img: {
      left: sceneWidth / 2 - centrePx * scale,
      top: floorY - photo.floorPx * scale,
      width: photo.width * scale,
      height: photo.height * scale,
    },
    floorY,
  };
}
